import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Undo2, X} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";
import {mapPartnerBotApiError, revokeBotInvite} from "../partnerBotApi";
import "../../../shared/finance/financeOperations.css";

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

export const PartnerBotRevokeDialog = ({open, invite, entity, onCancel, onRevoked}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const invokingElementRef = useRef(null);
    const submittingRef = useRef(false);
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const inviteId = invite?.id;

    useEffect(() => {
        setReason("");
        setError(null);
        setPending(false);
        submittingRef.current = false;
    }, [open, inviteId]);

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => firstFieldRef.current?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!submittingRef.current) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open]);

    if (!open) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (submittingRef.current) return;
        const trimmedReason = reason.trim();
        if (trimmedReason.length < 3) {
            setError({message: t("orgs.common.reasonTooShort")});
            return;
        }

        submittingRef.current = true;
        setPending(true);
        setError(null);
        try {
            await revokeBotInvite({inviteId, reason: trimmedReason});
            onRevoked();
        } catch (requestError) {
            setError(mapPartnerBotApiError(requestError));
        } finally {
            submittingRef.current = false;
            setPending(false);
        }
    };

    return createPortal(
        <div className="finance-dialog-backdrop">
            <section
                ref={dialogRef}
                className="finance-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
            >
                <form onSubmit={submit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{entity}</p>
                            <h2 id={titleId}>{t("orgs.partnerBot.revokeTitle")}</h2>
                        </div>
                        <button
                            type="button"
                            className="finance-icon-button"
                            aria-label={t("orgs.common.close")}
                            title={t("orgs.common.close")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>

                    <p>{t("orgs.partnerBot.revokeCopy")}</p>

                    <div className="finance-dialog-fields">
                        <label>
                            <span>{t("orgs.common.reason")}</span>
                            <input
                                ref={firstFieldRef}
                                value={reason}
                                disabled={pending}
                                autoComplete="off"
                                onChange={(event) => {
                                    setReason(event.target.value);
                                    setError(null);
                                }}
                            />
                        </label>
                    </div>

                    {error?.message ? <div className="finance-dialog-error" role="alert">{error.message}</div> : null}

                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                            {t("orgs.common.cancel")}
                        </button>
                        <button type="submit" className="admin-button organization-danger-button" disabled={pending}>
                            <Undo2 aria-hidden="true" size={15} /> {pending ? t("orgs.partnerBot.revoking") : t("orgs.common.revoke")}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
